import { DateTime } from "luxon";
import { useMemo } from "react";
import { useChosenDepartment } from "@/contexts/ChosenDepartment";
import type { SuggestionsLoadState } from "@/contexts/sidePanel/sidePanelTypes";
import { useWeekViewContext } from "@/contexts/WeekView/WeekViewContext";
import { useNurseTimeOff } from "@/hooks/useNurseTimeOff";
import { useRosterLoadState } from "@/hooks/useRosterLookup";
import { useScheduleCacheEnabled } from "@/hooks/useScheduleWeekRules";
import { getWeekStart } from "@/utils/dateUtils";
import { DEFAULT_WEEK_START_DAY, type ScheduleData } from "@/utils/models";
import { getFirstLoadedDate } from "@/utils/panelWeeks";
import { withScheduleTimeOff } from "@/utils/withScheduleTimeOff";

export type WeekEngineSchedule = {
	/** The weeks and people the in-browser engine runs on, and its cards' suggestion positions point into. */
	schedule: ScheduleData;
	/** The shown week's position in `schedule.weeks`; -1 while it isn't there. */
	weekIndex: number;
	/** The Sunday `schedule.weeks` start on: the cards place a suggestion's day as days from it. */
	startingDate: DateTime;
	/**
	 * What the suggestions wait for: the staff list `schedule.nurses` is, and the weeks either side of the shown one,
	 * which the engine reads (a shift the day before or after, days in a row) and the grid doesn't wait for.
	 */
	suggestionsLoad: SuggestionsLoadState;
};

/**
 * What the Week view's in-browser engine reads: the shared week data, so the old list with `staffing_schedule_cache`
 * off and the cached weeks around the shown week with it on, where any week of the schedule has suggestions.
 */
export const useWeekEngineSchedule = (): WeekEngineSchedule => {
	const { scheduleWeeks, scheduleWeekIndex, scheduleNurses, isLoading, isScheduleError } = useWeekViewContext();
	const { chosenDepartment } = useChosenDepartment();
	const staffListLoad = useRosterLoadState(chosenDepartment.department_id);
	// A week past either end of the schedule has no shift rows, and the engine reads every week it is given by row:
	// leaving those weeks out makes them "before the first week" or "after the last", which it already handles.
	const weeks = useMemo(() => scheduleWeeks.filter((week) => week.weeklyShifts.length > 0), [scheduleWeeks]);
	const isCacheEnabled = useScheduleCacheEnabled();
	const timeOffOf = useNurseTimeOff();
	// The engine reads each nurse's time off: with the cache on, the schedule's.
	const schedule = useMemo<ScheduleData>(
		() => ({ weeks, nurses: isCacheEnabled ? withScheduleTimeOff(scheduleNurses, timeOffOf) : scheduleNurses }),
		[weeks, scheduleNurses, isCacheEnabled, timeOffOf],
	);
	const firstLoadedDate = getFirstLoadedDate(weeks);
	// DEFAULT: with no week loaded there is no suggestion to place, so any Sunday does.
	const startingDate = useMemo(() => getWeekStart(firstLoadedDate ?? DateTime.now(), DEFAULT_WEEK_START_DAY), [firstLoadedDate]);
	const suggestionsLoad = useMemo(
		() => ({ isLoading: staffListLoad.isLoading || isLoading, isError: staffListLoad.isError, isWeekError: isScheduleError }),
		[staffListLoad.isLoading, staffListLoad.isError, isLoading, isScheduleError],
	);
	return { schedule, weekIndex: weeks.indexOf(scheduleWeeks[scheduleWeekIndex]), startingDate, suggestionsLoad };
};
